import { useCallback, useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { techdocsRdapVersionStoreApiRef } from '../apis/versionStore';
import { useTechDocsVersions } from '../hooks/useTechDocsVersions';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: 150,
  },
  select: {
    fontSize: '1rem',
  },
}));

// Forces the menu to always drop down from below the control instead of
// MUI's default of centering it on the selected item — with a ListSubheader
// in the list, that default anchoring made the menu jump up and overlap it.
const menuProps = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' } as const,
  transformOrigin: { vertical: 'top', horizontal: 'left' } as const,
};

export const TechDocsVersionSelector = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const versionStore = useApi(techdocsRdapVersionStoreApiRef);

  const entityId = {
    kind: entity.kind,
    namespace: entity.metadata.namespace ?? 'default',
    name: entity.metadata.name,
  };

  const { versions, loading, error } = useTechDocsVersions(entityId);
  const registeredRef = versions?.registeredRef;

  const [selectedRef, setSelectedRef] = useState('');

  // Once the registered branch is known, that's always the initial
  // selection — and it stays on the stock (already-built) docs path, since
  // the store is only written to when the user picks something else.
  useEffect(() => {
    if (registeredRef && !selectedRef) {
      setSelectedRef(registeredRef);
    }
  }, [registeredRef, selectedRef]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const ref = String(event.target.value);
      if (!ref || ref === 'undefined') {
        return;
      }
      setSelectedRef(ref);
      versionStore.set(entityId, ref === registeredRef ? undefined : ref);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versionStore, entityId.kind, entityId.namespace, entityId.name, registeredRef],
  );

  if (loading || error || !versions) {
    return (
      <FormControl
        variant="outlined"
        size="small"
        className={classes.formControl}
        disabled
      >
        <Select
          value="current"
          className={classes.select}
          MenuProps={menuProps}
          inputProps={{ 'aria-label': 'Documentation version' }}
        >
          <MenuItem value="current">
            {loading ? 'Loading versions…' : 'Current'}
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      variant="outlined"
      size="small"
      className={classes.formControl}
    >
      <Select
        value={selectedRef || registeredRef}
        onChange={handleChange}
        className={classes.select}
        MenuProps={menuProps}
        inputProps={{
          'aria-label': 'Documentation version',
        }}
      >
        <ListSubheader style={{ pointerEvents: 'none' }}>
          Branches
        </ListSubheader>
        {versions.branches.map(branch => (
          <MenuItem key={`branch:${branch}`} value={branch}>
            {branch}
            {branch === versions.registeredRef ? ' (default)' : ''}
          </MenuItem>
        ))}
        {versions.tags.length > 0 && (
          <ListSubheader style={{ pointerEvents: 'none' }}>
            Tags
          </ListSubheader>
        )}
        {versions.tags.map(tag => (
          <MenuItem key={`tag:${tag}`} value={tag}>
            {tag}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
